import { ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';

import { FinanceService } from '../../../services/finance.service';
import { AuthService } from '../../../services/auth.service';
import { DashboardWidgetLayout } from '../dashboard-grid/dashboard-grid';
import {
  DashboardTimeWindowOption,
  dashboardErrorMessage,
  dashboardLayoutStyle,
  formatDashboardValue,
} from '../../widget-and-graphs.models';

interface CashNode {
  label: string;
  value: number;
  color: string;
  y: number;
  height: number;
}

interface FlowDiagram {
  sources: CashNode[];
  uses: CashNode[];
  incoming: CashLink[];
  outgoing: CashLink[];
  centerHeight: number;
}

interface CashLink {
  label: string;
  value: number;
  color: string;
  path: string;
}

@Component({
  selector: 'app-dashboard-sankey-widget',
  standalone: false,
  templateUrl: './dashboard-sankey-widget.html',
  styleUrl: './dashboard-sankey-widget.scss',
})
export class DashboardSankeyWidget implements OnInit, OnDestroy {
  @Input() iconSvg = '';
  @Input() title = 'Where cash went';
  @Input() currencyCode = 'USD';
  @Input() locale = 'en-US';
  @Input() layout: DashboardWidgetLayout = { xs: { width: 12, height: 4 } };
  @Input() timeWindowOptions: DashboardTimeWindowOption[] = [
    { label: 'Last 30 days', duration: 30 },
    { label: 'Last 3 months', duration: 90 },
    { label: 'Last 12 months', duration: 365 },
  ];

  loading = true;
  error = '';
  selectedWindowIndex = 0;
  rangeOpen = false;
  hasCashActivity = false;
  cashAtEnd = 0;
  netProfit = 0;
  revenue = 0;
  profitFlow: FlowDiagram = this.emptyFlow();
  cashFlow: FlowDiagram = this.emptyFlow();

  private requestId = 0;
  private baseCurrencyCode: string | null = null;

  constructor(
    private readonly finance: FinanceService,
    private readonly auth: AuthService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  @HostBinding('attr.style')
  get layoutStyles(): string {
    return dashboardLayoutStyle(this.layout);
  }

  get selectedWindow(): DashboardTimeWindowOption {
    return this.timeWindowOptions[this.selectedWindowIndex]
      ?? { label: 'Last 30 days', duration: 30 };
  }

  ngOnInit(): void {
    void this.load();
  }

  ngOnDestroy(): void {
    this.requestId += 1;
  }

  selectWindow(index: number): void {
    this.selectedWindowIndex = index;
    this.rangeOpen = false;
    void this.load();
  }

  money(value: number): string {
    return formatDashboardValue(value, 'currency', this.currencyCode, this.locale);
  }

  private async load(): Promise<void> {
    const request = ++this.requestId;
    const today = new Date();
    const from = new Date(today);
    from.setUTCDate(from.getUTCDate() - this.selectedWindow.duration + 1);
    const query = {
      dateFrom: from.toISOString().slice(0, 10),
      dateTo: today.toISOString().slice(0, 10),
    };

    this.loading = true;
    this.error = '';
    try {
      const [cashFlow, profitLoss, currencyCode] = await Promise.all([
        this.finance.getCashFlow(query),
        this.finance.getProfitLoss(query),
        this.reportCurrencyCode(),
      ]);
      if (request !== this.requestId) return;

      this.currencyCode = currencyCode;
      const cash = cashFlow.summary;
      const profit = profitLoss.summary;
      this.cashAtEnd = this.number(cash['ending_cash']);
      this.netProfit = this.number(profit['net_profit']);
      this.revenue = this.number(profit['revenue']);
      this.profitFlow = this.buildProfitFlow(profit);
      this.cashFlow = this.buildCashFlow(
        cashFlow.lines,
        this.number(cash['beginning_cash']),
        this.cashAtEnd,
      );
      this.hasCashActivity = this.cashFlow.sources.length > 0 || this.cashFlow.uses.length > 0;
    } catch (error) {
      if (request !== this.requestId) return;
      this.error = dashboardErrorMessage(error);
      this.hasCashActivity = false;
    } finally {
      if (request === this.requestId) {
        this.loading = false;
        this.cdr.detectChanges();
      }
    }
  }

  private buildCashFlow(
    lines: Array<Record<string, unknown>>,
    opening: number,
    ending: number,
  ): FlowDiagram {
    const lineValue = (key: string): number => this.number(lines.find((line) => line['key'] === key)?.['current']);
    const movements = [
      { label: 'Opening cash', value: opening, color: '#206bc4' },
      { label: 'Net profit', value: lineValue('net-profit'), color: '#2fb344' },
      { label: 'Depreciation', value: lineValue('depreciation-adjustment'), color: '#74b816' },
      { label: 'Receivables & current assets', value: lineValue('change-current-assets'), color: '#f76707' },
      { label: 'Payables & current liabilities', value: lineValue('change-current-liabilities'), color: '#0ca678' },
      { label: 'Investing activities', value: lineValue('net-investing-cash'), color: '#f59f00' },
      { label: 'Financing activities', value: lineValue('net-financing-cash'), color: '#ae3ec9' },
    ];
    return this.buildFlow(movements, ending, 'Closing cash');
  }

  private buildProfitFlow(summary: Record<string, unknown>): FlowDiagram {
    const netProfit = this.number(summary['net_profit']);
    const movements = [
      { label: 'Sales revenue', value: this.number(summary['revenue']), color: '#206bc4' },
      { label: 'Other income', value: this.number(summary['other_income']), color: '#4299e1' },
      { label: 'Cost of revenue', value: -this.number(summary['cost_of_revenue']), color: '#f76707' },
      { label: 'Operating expenses', value: -this.number(summary['operating_expenses']), color: '#d63939' },
    ];
    return this.buildFlow(movements, netProfit, netProfit >= 0 ? 'Net profit' : 'Net loss');
  }

  private buildFlow(
    movements: Array<{ label: string; value: number; color: string }>,
    closing: number,
    closingLabel: string,
  ): FlowDiagram {
    const inflows = movements.filter((item) => item.value > 0.005);
    const outflows = movements.filter((item) => item.value < -0.005)
      .map((item) => ({ ...item, value: -item.value }));
    if (closing >= 0.005) {
      outflows.push({ label: closingLabel, value: closing, color: '#2fb344' });
    } else if (closing <= -0.005) {
      inflows.push({ label: closingLabel, value: -closing, color: '#d63939' });
    }

    const incomingTotal = inflows.reduce((sum, item) => sum + item.value, 0);
    const outgoingTotal = outflows.reduce((sum, item) => sum + item.value, 0);
    if (incomingTotal - outgoingTotal > 0.01) {
      outflows.push({ label: 'Unreconciled cash', value: incomingTotal - outgoingTotal, color: '#d63939' });
    } else if (outgoingTotal - incomingTotal > 0.01) {
      inflows.push({ label: 'Unreconciled cash', value: outgoingTotal - incomingTotal, color: '#d63939' });
    }

    if (!inflows.length && !outflows.length) return this.emptyFlow();

    // The statement reconciles opening cash plus signed movements to closing cash.
    // Split positive and negative movements so every visible link has a positive width.
    const total = inflows.reduce((sum, item) => sum + item.value, 0);
    const scale = 170 / Math.max(total, 0.01);
    const sources = this.placeNodes(inflows, scale);
    const uses = this.placeNodes(outflows, scale);
    const centerHeight = total * scale;

    let incomingY = 42;
    const incoming = sources.map((node) => {
      const link = {
        label: node.label,
        value: node.value,
        color: node.color,
        path: this.ribbon(194, node.y, 427, incomingY, node.height),
      };
      incomingY += node.height;
      return link;
    });

    let outgoingY = 42;
    const outgoing = uses.map((node) => {
      const link = {
        label: node.label,
        value: node.value,
        color: node.color,
        path: this.ribbon(443, outgoingY, 676, node.y, node.height),
      };
      outgoingY += node.height;
      return link;
    });
    return { sources, uses, incoming, outgoing, centerHeight };
  }

  private placeNodes(
    items: Array<{ label: string; value: number; color: string }>,
    scale: number,
  ): CashNode[] {
    let y = 42;
    return items.map((item) => {
      const height = item.value * scale;
      const node = { ...item, y, height };
      y += height + 12;
      return node;
    });
  }

  private ribbon(x1: number, y1: number, x2: number, y2: number, height: number): string {
    const c1 = x1 + (x2 - x1) * 0.48;
    const c2 = x2 - (x2 - x1) * 0.48;
    return `M ${x1} ${y1} C ${c1} ${y1} ${c2} ${y2} ${x2} ${y2} `
      + `L ${x2} ${y2 + height} C ${c2} ${y2 + height} ${c1} ${y1 + height} ${x1} ${y1 + height} Z`;
  }

  private number(value: unknown): number {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private async reportCurrencyCode(): Promise<string> {
    if (this.baseCurrencyCode) return this.baseCurrencyCode;
    const currency = (await this.auth.getCurrentOrganisation().catch(() => null))?.currency;
    if (typeof currency === 'string' && /^[A-Z]{3}$/i.test(currency.trim())) {
      this.baseCurrencyCode = currency.trim().toUpperCase();
      return this.baseCurrencyCode;
    }
    return this.finance.getDefaultCurrencyIso();
  }

  private emptyFlow(): FlowDiagram {
    return { sources: [], uses: [], incoming: [], outgoing: [], centerHeight: 0 };
  }

}
